import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Scan, Car, CarFront, CreditCard, ShieldAlert, Cpu, ClipboardCheck, AlertTriangle, Eye, Scale,
  FileSpreadsheet, History, Users, Building2, Settings, Smartphone, QrCode, LogOut, Menu, Sun, Moon, WifiOff, RefreshCw, X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { BranchSel } from "@/components/kit";
import Notifications from "@/components/Notifications";
import { getQueue, flush, isOffline, isForcedOffline, setForcedOffline } from "@/lib/offline";
import { fmtErr } from "@/lib/api";

const NAV = [
  ["Live Operations", [
    ["Dashboard", "/dashboard", LayoutDashboard, ["view"]],
    ["Lane Console", "/lanes", Scan, ["add"]],
    ["Parking Sessions", "/sessions", Car, ["view"]],
    ["Vehicles", "/vehicles", CarFront, ["view"]],
    ["Payments", "/payments", CreditCard, ["view"]],
  ]],
  ["Hardware & Control", [
    ["Barriers", "/barriers", ShieldAlert, ["view"]],
    ["Devices", "/devices", Cpu, ["view"]],
    ["Periodic Checks", "/checks", ClipboardCheck, ["view"]],
    ["Exceptions", "/exceptions", AlertTriangle, ["view"]],
    ["Watchlist", "/watchlist", Eye, ["view"]],
  ]],
  ["Finance & Analytics", [
    ["Reconciliation", "/reconciliation", Scale, ["view_financial"]],
    ["Reports", "/reports", FileSpreadsheet, ["view"]],
    ["Audit Trail", "/audit", History, ["view_technical", "view_financial", "manage_users"]],
  ]],
  ["Administration", [
    ["Branches", "/branches", Building2, ["view"]],
    ["Users", "/users", Users, ["manage_users"]],
    ["Settings", "/settings", Settings, ["view"]],
    ["Roving Attendant", "/attendant", Smartphone, ["add"]],
  ]],
];

function Sidebar({ onNav }) {
  const { can } = useAuth();
  return (
    <nav className="flex h-full flex-col overflow-y-auto px-3 pb-6">
      <div className="flex items-center gap-2.5 px-2 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-amber-500 font-mono text-lg font-black text-slate-950">P</div>
        <div>
          <div className="font-display text-base font-extrabold tracking-tight text-white">PARKINGCORE</div>
          <div className="font-mono text-[10px] text-slate-400">ParkingCore.mistermicpos.co</div>
        </div>
      </div>
      {NAV.map(([group, items]) => {
        const vis = items.filter((i) => can(...i[3]));
        if (!vis.length) return null;
        return (
          <div key={group} className="mt-4">
            <div className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{group}</div>
            {vis.map(([name, path, Icon]) => (
              <NavLink
                key={path}
                to={path}
                onClick={onNav}
                data-testid={`nav-${path.slice(1)}`}
                className={({ isActive }) =>
                  `mb-0.5 flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${
                    isActive ? "bg-amber-500/15 text-amber-400" : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {name}
              </NavLink>
            ))}
          </div>
        );
      })}
      <a href="/portal" target="_blank" rel="noreferrer" data-testid="nav-portal" className="mt-4 flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-slate-300 hover:bg-white/5">
        <QrCode className="h-4 w-4" /> Customer Portal
      </a>
    </nav>
  );
}

function OfflineBar() {
  const [q, setQ] = useState(getQueue().length);
  const [off, setOff] = useState(isOffline());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const upd = () => {
      setQ(getQueue().length);
      setOff(isOffline());
    };
    const sync = async () => {
      upd();
      if (!isOffline() && getQueue().length) {
        try {
          const r = await flush();
          if (r) toast.success(`Synced ${r.filter((x) => x.status === "synced").length} offline transaction(s); ${r.filter((x) => x.status === "duplicate").length} duplicate(s) skipped`);
        } catch (e) {
          toast.error(fmtErr(e));
        }
        upd();
      }
    };
    window.addEventListener("pc-queue", sync);
    window.addEventListener("online", sync);
    window.addEventListener("offline", upd);
    const t = setInterval(sync, 30000);
    return () => {
      window.removeEventListener("pc-queue", sync);
      window.removeEventListener("online", sync);
      window.removeEventListener("offline", upd);
      clearInterval(t);
    };
  }, []);
  if (!off && !q) return null;
  return (
    <div data-testid="offline-banner" className={`flex flex-wrap items-center gap-3 px-4 py-2 text-sm font-semibold ${off ? "bg-amber-500 text-slate-950" : "bg-cyan-600 text-white"}`}>
      <WifiOff className="h-4 w-4" />
      {off ? "OFFLINE MODE — transactions are queued locally and labelled OFFLINE" : "Back online"} • <span data-testid="offline-queue-count">{q}</span> queued
      {isForcedOffline() && (
        <button data-testid="offline-go-online" className="underline" onClick={() => setForcedOffline(false)}>Go online</button>
      )}
      {!off && q > 0 && (
        <button data-testid="offline-sync-now" disabled={busy} className="inline-flex items-center gap-1 underline" onClick={async () => { setBusy(true); window.dispatchEvent(new Event("pc-queue")); setBusy(false); }}>
          <RefreshCw className="h-3.5 w-3.5" /> Sync now
        </button>
      )}
    </div>
  );
}

export default function Shell() {
  const { user, logout, branch, setBranch } = useAuth();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(document.documentElement.classList.contains("dark"));
  const nav = useNavigate();
  const toggleTheme = () => {
    const d = !dark;
    document.documentElement.classList.toggle("dark", d);
    localStorage.setItem("pc_theme", d ? "dark" : "light");
    setDark(d);
  };
  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 bg-[hsl(var(--sidebar))] lg:block">
        <Sidebar />
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[hsl(var(--sidebar))]">
            <button className="absolute right-3 top-5 text-slate-400" onClick={() => setOpen(false)} data-testid="mobile-nav-close"><X className="h-5 w-5" /></button>
            <Sidebar onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <OfflineBar />
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border/60 bg-background/85 px-4 py-2.5 backdrop-blur-xl">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-nav-open"><Menu className="h-5 w-5" /></Button>
          <BranchSel value={branch} onChange={setBranch} testid="global-branch-select" className="w-40 sm:w-64" />
          <div className="ml-auto flex items-center gap-1.5">
            <Notifications />
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">{dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</Button>
            <div className="hidden text-right sm:block" data-testid="current-user">
              <div className="text-sm font-semibold leading-tight">{user.name}</div>
              <div className="text-[11px] text-muted-foreground">{user.role_label}</div>
            </div>
            <Button variant="ghost" size="icon" data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }}><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
